import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DatabaseBackup, CheckCircle2, AlertTriangle, Clock, MinusCircle, FolderLock, Link2 } from "lucide-react";
import { fmtDateTime } from "@/lib/date";

const HEALTH = {
  green: ["Healthy — backed up in the last 26 hours", "bg-emerald-50 text-emerald-800", CheckCircle2],
  amber: ["Overdue — no successful backup in 26 hours", "bg-amber-50 text-amber-800", Clock],
  red: ["Last backup failed", "bg-red-50 text-red-800", AlertTriangle],
  none: ["No backup yet", "bg-slate-100 text-slate-700", MinusCircle],
};
const DRIVE_MSG = {
  connected: ["success", "Google Drive connected. Now select the backup folder."],
  wrong_account: ["error", "That Google account isn't sean.rina@gmail.com — access was revoked."],
  failed: ["error", "Google Drive connection failed."], expired: ["error", "Connection link expired — try again."],
  cancelled: ["error", "Google Drive connection was cancelled."],
};

function loadPicker() {
  return new Promise((resolve, reject) => {
    const done = () => window.gapi.load("picker", { callback: resolve, onerror: reject });
    if (window.gapi) { done(); return; }
    const s = document.createElement("script");
    s.src = "https://apis.google.com/js/api.js";
    s.onload = done; s.onerror = reject;
    document.body.appendChild(s);
  });
}

async function pickFolder() {
  const cfg = (await api.get("/backups/drive/picker")).data;
  await loadPicker();
  const g = window.google.picker;
  return new Promise((resolve) => {
    const view = new g.DocsView(g.ViewId.FOLDERS).setSelectFolderEnabled(true).setIncludeFolders(true)
      .setMimeTypes("application/vnd.google-apps.folder").setParent("root");
    new g.PickerBuilder().addView(view).setOAuthToken(cfg.token).setDeveloperKey(cfg.api_key).setAppId(cfg.app_id)
      .setTitle("Select 'Team Smith Hub — Secure Database Backups'")
      .setCallback((d) => {
        if (d.action === g.Action.PICKED) resolve(d.docs[0].id);
        if (d.action === g.Action.CANCEL) resolve(null);
      }).build().setVisible(true);
  });
}

function Facts({ st }) {
  const s = st.state || {};
  const last = s.last_success;
  return (
    <div className="text-xs text-slate-600 grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="backup-facts">
      <span>Last success: <b data-testid="backup-last-success">{s.last_success_at ? fmtDateTime(s.last_success_at) : "—"}</b></span>
      <span>Last attempt: {s.last_run_at ? `${fmtDateTime(s.last_run_at)} · ${s.last_run_status}` : "—"}</span>
      {last && <span>Size {(last.size / 1048576).toFixed(1)} MB · {last.collections} tables · {last.documents} records</span>}
      {last && <span>Copies kept in Drive: {last.kept}</span>}
      <span>Drive: {st.drive ? `connected as ${st.drive.email}` : "not connected"}</span>
      <span>Folder: {s.folder ? `${s.folder.name} · owner ${s.folder.owner} · not shared` : "not verified"}</span>
      <span className="sm:col-span-2">Keeps {st.retention.daily_days} daily + {st.retention.monthly_months} monthly copies.
        Encrypted with Sean's offline key before upload.</span>
      {s.last_error && <span className="sm:col-span-2 text-red-700 break-words" data-testid="backup-last-error">{s.last_error}</span>}
    </div>
  );
}

export default function BackupPanel() {
  const [st, setSt] = useState(null);
  const load = useCallback(async () => {
    try { setSt((await api.get("/backups/status")).data); } catch { /* not the owner */ }
  }, []);
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search).get("backup_drive");
    if (p && DRIVE_MSG[p]) { toast[DRIVE_MSG[p][0]](DRIVE_MSG[p][1]); window.history.replaceState({}, "", window.location.pathname); }
  }, [load]);
  useEffect(() => {
    if (!st?.running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [st?.running, load]);
  const call = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Something went wrong"); }
    load();
  };
  const connect = () => call(async () => { window.location.href = (await api.get("/backups/drive/connect")).data.authorization_url; });
  const select = () => call(async () => {
    const id = await pickFolder();
    if (!id) throw new Error("No folder selected");
    await api.post("/backups/drive/folder", { folder_id: id });
  }, "Backup folder verified: owned by sean.rina@gmail.com and not shared");
  const run = () => call(async () => { await api.post("/backups/run"); setSt((s) => ({ ...s, running: true })); }, "Backup started");
  if (!st) return null;
  const [label, cls, Icon] = HEALTH[st.health] || HEALTH.none;
  const missing = Object.entries(st.config).filter(([, v]) => !v).map(([k]) => k);
  return (
    <Card className="p-4 sm:p-6 space-y-3" data-testid="backup-panel">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 justify-between">
        <div>
          <h2 className="font-serif text-xl text-[#0A1F44] flex items-center gap-2"><DatabaseBackup className="w-5 h-5" />Database backups</h2>
          <p className="text-xs text-slate-500 mt-0.5">Encrypted copies of all hub data, sent to Sean's private Google Drive folder.</p>
        </div>
        <span data-testid="backup-health" className={`self-start inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${cls}`}><Icon className="w-3.5 h-3.5" />{label}</span>
      </div>
      <Facts st={st} />
      {missing.length > 0 && <p className="text-xs text-amber-800 bg-amber-50 rounded-md p-2" data-testid="backup-missing-config">Setup still needed: {missing.join(", ")}</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={connect} data-testid="backup-connect-drive" className="rounded-full h-10"><Link2 className="w-4 h-4 mr-2" />{st.drive ? "Reconnect Google Drive" : "Connect Google Drive"}</Button>
        <Button variant="outline" onClick={select} disabled={!st.drive} data-testid="backup-select-folder" className="rounded-full h-10"><FolderLock className="w-4 h-4 mr-2" />Select backup folder</Button>
        <Button onClick={run} disabled={st.running || !st.state?.folder} data-testid="backup-run-now" className="rounded-full bg-[#0A1F44] text-white h-10">
          <DatabaseBackup className={`w-4 h-4 mr-2 ${st.running ? "animate-pulse" : ""}`} />{st.running ? "Backing up…" : "Back up now"}
        </Button>
      </div>
    </Card>
  );
}
